import React, { FC, useCallback, useEffect, useState } from 'react';
import { useUserContext } from '../../../providers/UserProvider';
import { Prompt } from '../../shared/Prompt';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './AccountFinancialLimitReviewPrompt.scss';
import { useUserProfileUpdateMutation } from '../../../libs/graphql/baseAppAPI/mutations/__generated__/user-profile-update.mutation.generated';
import { APP_URLS, xTenantId } from '../../../consts';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../../hooks/snackbar';
import { useUserBudgetLimitsQuery } from '../../../libs/graphql/baseAppAPI/queries/__generated__/obtaining-limits.query.generated';
import { useAuth } from 'react-oidc-context';
import { Paragraph } from '../../shared/Paragraph';
import { cashierModal } from '../../../state/cashier';
import { useSelector } from 'react-redux';

export const AccountFinancialLimitReviewPrompt: FC = () => {
  const { consents, fetchUserProfile, userProfileId } = useUserContext();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [showPrompt, setShowPrompt] = useState(false);
  const { addSnack } = useSnackbar();
  const auth = useAuth();
  const profileId = userProfileId || (auth.user?.profile?.user_profile_id as string);
  const [depositLimitExists, setDepositLimitExists] = useState(false);
  const location = useLocation();

  const { isOpen } = useSelector(cashierModal);

  useUserBudgetLimitsQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      xTenantId,
      userProfileId: profileId,
    },
    onCompleted: (res) => {
      const hasMonetaryLimit = res.userBudgetLimits?.some((limit) => limit?.mediatorType === 'monetary');

      setDepositLimitExists(hasMonetaryLimit || false);
    },
  });

  const [userProfileUpdateMutation] = useUserProfileUpdateMutation({
    onCompleted: async (data) => {
      if (!data) return;
      await fetchUserProfile();
      setShowPrompt(false);
    },
    onError: (error) => {
      console.error(error);

      return handleError();
    },
  });

  const handleError = (): void => {
    addSnack({
      type: 'error',
      theme: 'dark',
      message: `Oops, something's gone wrong`,
    });
    navigate(APP_URLS.home);

    return;
  };

  useEffect(() => {
    if (!consents) {
      return;
    }

    const lastReview = consents?.lastFinancialLimitReviewOnUtc;

    if (!lastReview) {
      return;
    }

    const lastReviewDate = new Date(lastReview);
    const oneYearAgo = new Date();

    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    if (!depositLimitExists && lastReviewDate < oneYearAgo) {
      setShowPrompt(true);
    }
  }, [consents, depositLimitExists, isOpen]);

  const handleAcknowledge = async (): Promise<void> => {
    const currentDate = new Date().toISOString();

    await userProfileUpdateMutation({
      variables: {
        userProfileId,
        xTenantId,
        userProfileUpdateInfo: {
          lastFinancialLimitReviewOnUtc: currentDate,
        },
      },
    });
  };

  const handleClose = useCallback((): void => {
    setShowPrompt(false);
  }, []);

  const navigateWithBackground = useCallback(
    (path: string): void => {
      handleClose();
      navigate(path, {
        state: { previousLocation: location },
      });
    },
    [handleClose, navigate, location]
  );

  const handleSetDepositLimit = async (): Promise<void> => {
    await handleAcknowledge();
    navigateWithBackground(`${APP_URLS.myAccount.gamblingControls}?edit=monetary`);
  };

  return (
    <Prompt
      PrimaryButton={{ text: t('account-financial-limit-review.button.1'), onClick: handleSetDepositLimit }}
      SecondaryButton={{ text: t('account-financial-limit-review.button.2'), onClick: handleAcknowledge }}
      show={showPrompt}
      title={t('account-financial-limit-review-title')}
      onClose={handleClose}
    >
      <div className="account-financial-limit-review-content">
        <Paragraph className="account-financial-limit-review__text">
          {t('account-financial-limit-review-paragraph1')}
        </Paragraph>

        <Paragraph className="account-financial-limit-review__text">
          {t('account-financial-limit-review-paragraph2')}
        </Paragraph>

        <Paragraph className="account-financial-limit-review__text">
          {t('account-financial-limit-review-paragraph3')}
        </Paragraph>
      </div>
    </Prompt>
  );
};
